import { MeterFormat, MeterType } from '@fsp-lib/shared/enums';
import {
  FspError,
  MeterReconfirmItem,
  MeterReconfirmMeter,
  MeterRuleError,
  MeterRuleErrorData,
  MeterValidationAcknowledgement,
  MeterValidationErrorGroups,
} from '@fsp-lib/shared/models';

export const METER_TIME_HIGH_RULE_CODE = 'METER_TIME_HIGH';
export const METER_RECONFIRM_REASON_MIN_LENGTH = 3;
export const METER_RECONFIRM_REASON_MAX_LENGTH = 250;

const ACKNOWLEDGEMENT_DATA_FIELD_PREFIX = 'metervalidationacknowledgement.';
const GROUPED_METER_VALUE = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/;
const PLAIN_METER_VALUE = /^\d+(\.\d{1,2})?$/;

// en-US for every operator culture: the legacy meter inputs accept only '.' decimals.
const hoursFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 1,
});
const thresholdFormat = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});

type ScreenMeterItem = MeterReconfirmItem<{
  meterIn?: unknown;
  meterOut?: unknown;
}>;

export function buildMeterReconfirmItems<T extends MeterReconfirmMeter>(
  reconfirm: MeterRuleError[],
  meters: T[],
  previous?: MeterReconfirmItem<T>[],
): MeterReconfirmItem<T>[] {
  return reconfirm.map(({ data }) => {
    const isSameMeter = (candidate: {
      meterIndex: number;
      meterType: MeterType;
    }) =>
      candidate.meterType === data.meterType &&
      candidate.meterIndex === data.meterIndex;
    const meter = meters.find(isSameMeter) ?? null;
    const label = meter
      ? meter.title
      : getMeterReconfirmLabel(data.meterType, data.meterIndex);
    const isSegmentTotal = meter
      ? isReEnteredValueTotal(meter.meterType, meter.meterFormat)
      : false;

    return {
      choiceError: false,
      data,
      fixLabel: isSegmentTotal
        ? `Fix the ${label} segments`
        : 'Fix the Out reading',
      isSegmentTotal,
      label,
      mathLine: formatMeterReconfirmMath(label, data),
      meter,
      meterIndex: data.meterIndex,
      meterType: data.meterType,
      outLooksUnsetHint: formatOutLooksUnsetHint(label, data, isSegmentTotal),
      path: previous?.find(isSameMeter)?.path ?? null,
      reEnteredValue: '',
      reEnteredValueError: false,
      reEnteredValueLabel: isSegmentTotal
        ? `Enter the ${label} total again`
        : `Enter the ${label} In again`,
      ruleCode: data.ruleCode,
    };
  });
}

export function buildMeterValidationAcknowledgement(
  items: ScreenMeterItem[],
  reason: string | null | undefined,
): MeterValidationAcknowledgement | null {
  const acknowledged = items
    .filter(isMeterReconfirmItemAcknowledged)
    .map((item) => ({
      item,
      value: parseReEnteredMeterValue(item.reEnteredValue),
    }))
    .filter(({ value }) => value !== null);

  if (acknowledged.length === 0) {
    return null;
  }

  return {
    reason: (reason ?? '').trim(),
    reEnteredValues: acknowledged.map(({ item, value }) => ({
      meterIndex: item.meterIndex,
      meterType: item.meterType,
      value,
    })),
    ruleCodes: [...new Set(acknowledged.map(({ item }) => item.ruleCode))],
  };
}

/** The server acks by rule code, so a changed meter still over the limit needs it too. */
export function doesMeterReconfirmItemNeedConfirmation(
  item: ScreenMeterItem,
): boolean {
  if (item.meter === null) {
    return true;
  }

  return (
    !isMeterReconfirmItemChanged(item) ||
    isMeterStillOverLimit(item.meter.meterOut, item.meter.meterIn, item.data)
  );
}

export function formatMeterReconfirmMath(
  label: string,
  data: MeterRuleErrorData,
): string {
  const days = getReservedDays(data);
  const total = data.total ?? data.in - data.out;
  const threshold = thresholdFormat.format(data.threshold ?? 0);
  const perDay = days > 1 ? ' a day' : '';

  return (
    `${label}: ${hoursFormat.format(data.in)} − ` +
    `${hoursFormat.format(data.out)} = ${hoursFormat.format(total)} hrs ` +
    `on a ${days}-day reservation (expected up to about ${threshold}${perDay})`
  );
}

/** Null for a segment meter: Out cannot change its total, so the advice is wrong. */
export function formatOutLooksUnsetHint(
  label: string,
  data: MeterRuleErrorData,
  isSegmentTotal: boolean,
): string | null {
  if (isSegmentTotal || data.outLooksUnset !== true) {
    return null;
  }

  return (
    `If ${hoursFormat.format(data.in)} is the aircraft's current meter and ` +
    "not this flight's time, enter the meter reading before this flight as " +
    `${label} Out.`
  );
}

/** A blocked Save must say why, so each blocking item gets a message; null otherwise. */
export function getMeterReconfirmGateMessage(
  item: ScreenMeterItem,
): string | null {
  if (isMeterReconfirmItemUnresolved(item)) {
    return `Choose how to fix the ${item.label} meter time.`;
  }

  const isReEntryMissing =
    isMeterReconfirmItemAcknowledged(item) &&
    parseReEnteredMeterValue(item.reEnteredValue) === null;
  return isReEntryMissing ? `${item.reEnteredValueLabel}.` : null;
}

/** Fallback for an unmatched error; mirrors the legacy createLabelAndTitle. */
export function getMeterReconfirmLabel(
  meterType: MeterType,
  meterIndex: number,
): string {
  switch (meterType) {
    case MeterType.Tach:
      return `Engine ${meterIndex} Tach`;
    case MeterType.Air_Time:
      return 'Air Time';
    case MeterType.HeaterHobbs:
      return 'Heater Hobbs';
    case MeterType.Flight_Time:
      return 'Flight Time';
    default:
      return 'Hobbs';
  }
}

/** Only these items send an acknowledgement, a re-entered value and a reason. */
export function isMeterReconfirmItemAcknowledged(
  item: ScreenMeterItem,
): boolean {
  return (
    item.path === 'confirm' && doesMeterReconfirmItemNeedConfirmation(item)
  );
}

export function isMeterReconfirmItemChanged(item: ScreenMeterItem): boolean {
  if (item.meter === null) {
    return false;
  }

  return !isMeterUnchangedSinceRuleError(
    item.meter.meterOut,
    item.meter.meterIn,
    item.data,
  );
}

/** A resend of an unresolved item would only bring back the same 400. */
export function isMeterReconfirmItemUnresolved(item: ScreenMeterItem): boolean {
  return (
    item.path !== 'confirm' && doesMeterReconfirmItemNeedConfirmation(item)
  );
}

export function isMeterReconfirmReasonValid(
  reason: string | null | undefined,
): boolean {
  const length = (reason ?? '').trim().length;
  return (
    length >= METER_RECONFIRM_REASON_MIN_LENGTH &&
    length <= METER_RECONFIRM_REASON_MAX_LENGTH
  );
}

/** The only runtime check on the untyped payload, so it covers every field read later. */
export function isMeterRuleErrorData(
  value: unknown,
): value is MeterRuleErrorData {
  if (!isObject(value)) {
    return false;
  }

  return (
    typeof value['ruleCode'] === 'string' &&
    typeof value['meterType'] === 'number' &&
    typeof value['meterIndex'] === 'number' &&
    typeof value['in'] === 'number' &&
    typeof value['out'] === 'number'
  );
}

/** Blank or null never counts as unchanged: Number('') and Number(null) are both 0. */
export function isMeterUnchangedSinceRuleError(
  meterOut: unknown,
  meterIn: unknown,
  data: MeterRuleErrorData,
): boolean {
  const out = toMeterNumber(meterOut);
  const meterInValue = toMeterNumber(meterIn);

  return (
    out !== null &&
    meterInValue !== null &&
    out === data.out &&
    meterInValue === data.in
  );
}

/** The server compares the exact In − Out of 2-decimal values; rounding here only
 * removes float error. A total at the limit is not over it. */
export function isMeterStillOverLimit(
  meterOut: unknown,
  meterIn: unknown,
  data: MeterRuleErrorData,
): boolean {
  const out = toMeterNumber(meterOut);
  const meterInValue = toMeterNumber(meterIn);
  if (out === null || meterInValue === null || data.threshold === null) {
    return false;
  }

  const total = Math.round((meterInValue - out) * 100) / 100;
  return total > data.threshold * getReservedDays(data);
}

/** Mirrors the server: Air Time and Flight Time segment formats re-enter the total. */
export function isReEnteredValueTotal(
  meterType: MeterType,
  meterFormat: MeterFormat | null | undefined,
): boolean {
  const isSegmentFormat =
    meterFormat === MeterFormat.MinuteBasedSegments ||
    meterFormat === MeterFormat.DateTimeBasedSegments;
  return (
    isSegmentFormat &&
    (meterType === MeterType.Air_Time || meterType === MeterType.Flight_Time)
  );
}

export function parseReEnteredMeterValue(value: unknown): number | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (PLAIN_METER_VALUE.test(trimmed)) {
    return Number(trimmed);
  }

  return GROUPED_METER_VALUE.test(trimmed)
    ? Number(trimmed.replace(/,/g, ''))
    : null;
}

export function splitMeterValidationErrors(
  errors: unknown,
): MeterValidationErrorGroups {
  const groups: MeterValidationErrorGroups = {
    acknowledgement: [],
    block: [],
    other: [],
    reconfirm: [],
  };

  if (!Array.isArray(errors)) {
    return groups;
  }

  errors.filter(isObject).forEach((entry) => {
    const error = entry as unknown as FspError;
    const data = error.additionalData;

    if (isAcknowledgementError(error)) {
      groups.acknowledgement.push(error);
    } else if (!isMeterRuleErrorData(data)) {
      groups.other.push(error);
    } else if (error.overridable !== true) {
      groups.block.push({ data, message: error.message });
    } else if (data.ruleCode === METER_TIME_HIGH_RULE_CODE) {
      groups.reconfirm.push({ data, message: error.message });
    } else {
      groups.other.push(error);
    }
  });

  return groups;
}

function getReservedDays(data: MeterRuleErrorData): number {
  return typeof data.reservedDays === 'number' && data.reservedDays >= 1
    ? data.reservedDays
    : 1;
}

function isAcknowledgementError(error: FspError): boolean {
  return (
    typeof error.dataField === 'string' &&
    error.dataField.toLowerCase().startsWith(ACKNOWLEDGEMENT_DATA_FIELD_PREFIX)
  );
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toMeterNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
